"use client";

// Specific sub-barrel, not the `use-api` mega-barrel — avoids the CommonJS/jest
// require cycle that leaves this `undefined` in tests (see use-client-self-contact).
import { useAccessibleClients } from "@/lib/hooks/api/client-portal";
import { useAuthenticatedUser } from "@/lib/hooks/auth/use-authenticated-user";
import type { AuthenticatedUser } from "@/lib/types/unified";

interface UseClientAccessReturn {
  hasContactAccess: boolean;
  isLoading: boolean;
  isClientUser: boolean;
  appUser: AuthenticatedUser | null;
  accessibleClientsQuery: any;
}

/**
 * Custom hook that manages client access logic and state
 * Handles the business logic for determining if a client user has access.
 *
 * State is DERIVED directly from the source signals (auth-loading + the access
 * query) every render — it is intentionally NOT mirrored into a useState +
 * effect. A separate state mirror settled out of order and caused a
 * flash-of-"No Client Access Found": when `isClientUser` flipped true a frame
 * after a stale `isLoading` had already been set false (via the `!isClientUser`
 * branch) and before the access query resolved, the guard saw
 * `isClientUser=true, hasContactAccess=false` and rendered the no-access state
 * for a frame. Deriving keeps `isLoading` true until the decision is actually
 * resolvable, so the no-access state can only appear once access is truly known.
 */
export function useClientAccess(): UseClientAccessReturn {
  const { appUser, isClientUser, isLoading: isAuthLoading } = useAuthenticatedUser();

  // Use the accessible clients hook to get real data
  const accessibleClientsQuery = useAccessibleClients();

  const accessResolved = !!accessibleClientsQuery.data || !!accessibleClientsQuery.error;

  // Loading until the access decision can actually be made:
  // - auth not resolved yet → we don't even know the role
  // - role is client → wait for the access query to settle (data OR error)
  // - role is not a client → nothing to wait on
  const isLoading = isAuthLoading || (isClientUser && !accessResolved);

  // Only trust hasAccess once the query has data AND we know the user is a client.
  const hasContactAccess =
    isClientUser && accessibleClientsQuery.data ? accessibleClientsQuery.data.hasAccess : false;

  return {
    hasContactAccess,
    isLoading,
    isClientUser,
    appUser,
    accessibleClientsQuery,
  };
}
